// shared/src/prompts/m-consensus-refine.ts
// Chain V2 — market consensus path, refine stage (post-tools, post-skeptic).
// Symmetric mirror of m-contrarian-refine for the consensus path.
//
// Per design-notes L7/L14/L17.

import { z } from 'zod';
import { HorizonEnum, ThesisStatusEnum, HighMedLowEnum, RegimeEnum } from '../resolvers/llm-enum-coerce.js';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import { hoistMisnestedFields } from '../resolvers/hoist-misnested.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import { renderNewsBlock } from './_news-render.js';
import type { LlmNewsItem } from '../news/types.js';

export const VERSION = '1.5.0';   // 2026-07-10 — cascade_nodes .min(1)→.max(12).default([]) (omission-safe; empty→[] + EMPTY_CASCADE WARN badge, not a brief kill). // Prior: 1.4.0 (2026-06-11 — schema rigidity hardening: probability + regime_confidence llmNumber-coerced; refinement_rationale .min(1)→z.string() (Zod-only floor, NO market-emit validator twin — safe; field stays required). See docs/llm-schema-rigidity-hardening-2026-06-11.md). // Prior: 1.3.0 (2026-05-26 #107 — INDICATOR VOCAB + OBSERVABLE = STATE LEVEL). Matches m-contrarian-refine.
export const STEP    = 'm.consensus.refine';
export const MODEL   = 'deepseek-v4-flash';
// 2026-07-31 — raised 12000 → 24000, matching its analyst sibling. Same model,
// same vendor reasoning drift, same fire. This step is the most exposed of the
// remaining M-stage family: its largest successful response was 7325 tok = 0.61
// of the old cap, and the analysts went from 0.67 to blown overnight. Raised now
// rather than waiting for it to break, because a ceiling costs nothing unused.
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 24000)
export const ROLE    = 'refine' as const;
export const DESC    = 'Re-emits the full consensus thesis reconciled with skeptic critique + tool results' as const;   // #45 registry — one-line operator description

const BaseOutputSchema = z.object({
  thesis_headline:       z.string().min(1),
  thesis_paragraph:      z.string().min(1),
  basis: z.object({
    evidence:            z.string().min(1),
    catalyst:            z.string(),
    invalidation:        z.string(),
  }),
  priced_explanation:    z.string().min(1),
  probability:           llmNumber(z.number().min(0).max(100)),    // D38 (2026-05-28): dropped .int(). 2026-06-11: llmNumber-coerced.
  // Session 5T (2026-05-28) D13: widened to include '2w' — see m-contrarian-analyst.ts for rationale.
  horizon:               HorizonEnum,
  conditional:           z.string(),
  cascade_prose:         z.string(),
  // Session 5T (2026-05-28) D15: loosened .min(3)→.min(1). Prompt still requests 3-5 typical (normative).
  // 2026-06-23: .max(5)→.max(12). The 5 was a CascadeDiagram display-density spec ("3-5 nodes"), NOT a data
  // cap — the renderer already clamps (CascadeDiagram.resolveCascadeNodes slice 0,5; ZoneThesisRipples chips
  // wrap). A 6-node crash-day cascade hard-failed a whole brief at m.consensus.refine (brief 019ef4b1). 12 is
  // a runaway-junk guard that never trips on real output. See docs/prompt-arbitrary-limits-audit-2026-05-28.md.
  cascade_nodes:         z.array(llmStringItem).max(12).default([]),   // 2026-07-10: omission-safe (see m-consensus-analyst). Empty/omitted → [] + EMPTY_CASCADE WARN badge, not a brief kill.
  cross_portfolio_thread: z.string(),
  top_risk:              z.string(),
  risk_mit:              z.string(),
  thesis_status:         ThesisStatusEnum,
  thesis_div:            HighMedLowEnum,
  regime:                RegimeEnum,
  regime_confidence:     llmNumber(z.number().min(0).max(1)),
  // Session 5T (2026-05-28) D15: loosened .min(2)→.min(1). See macro-prediction-emit.ts:152.
  macro_predictions:     z.array(z.unknown()).min(1).max(5),
  refinement_rationale:  z.string(),   // 2026-06-11: .min(1) dropped (cosmetic audit-trail prose; NOT in market-emit PATH_REQUIRED_FIELDS, so Zod-only → no C002 split). Still required.
}).passthrough();

// Dropped-middle-brace repair — see m-contrarian-analyst.ts (same shape/root cause).
// Lifts any basis-nested top-level field back up before validation; no-op on clean
// output. docs/investigations/m-contrarian-analyst-basis-nesting-2026-08-10.md
export const OutputSchema = z.preprocess(
  (v) => hoistMisnestedFields(v, 'basis', Object.keys(BaseOutputSchema.shape), { step: STEP }),
  BaseOutputSchema,
);
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# MARKET CONSENSUS REFINE (V2)

## ROLE
You are refining the CONSENSUS analyst's draft of a market-level macro thesis. The user message contains:
  · own_draft        — the consensus analyst's initial draft (your own prior emit)
  · own_skeptic      — disconfirming_evidence + cross_fed_counter + conviction from the consensus skeptic
  · tool_results     — outputs from request_tools[] (merged + deduped across both paths)
  · macro + news     — current payload
  · grounding facts

Your job: emit the FULL refined consensus thesis. Treat the skeptic as a structural counterweight. Treat tool_results as ground truth.

## REFINEMENT DISCIPLINE
- STEEL-MAN PRESERVED. Refine consensus into its strongest defensible form. If tool data fully invalidates the consensus headline, REWRITE to the most defensible reduced claim (or set thesis_headline="" if no defensible refined version exists).
- factual_error skeptic cites MANDATORY-fix.
- reasonability cites: tighten OR address with payload-cited mechanism.
- opposing_draft (contrarian) cross-fed cites: strengthen consensus's basis evidence OR concede the specific claim.
- refinement_rationale MUST cite the specific tool result OR skeptic point that drove each material change.

## NO CALIBRATION (L14). Reason FRESH from this brief's payload + skeptic + tools.

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary outside)
{
  "thesis_headline":    "<≤25w refined consensus headline>",
  "thesis_paragraph":   "<100-120w refined.>",
  "basis": {
    "evidence":         "<≤14w refined payload alignment>",
    "catalyst":         "<≤12w refined forward catalyst (≥ TODAY) — what would CONFIRM consensus>",
    "invalidation":     "<≤12w refined falsifier — what would BREAK consensus>"
  },
  "priced_explanation": "<consensus enum>: <≤25w rationale>",
  "probability":        50,
  "horizon":            "1w | 1m | 1q",
  "conditional":        "<≤18w>",
  "cascade_prose":      "<60-80w refined A→B→C consensus chain>",
  "cascade_nodes":      ["<≤6w>", "<≤6w>", "<≤6w>"],
  "cross_portfolio_thread": "<60-80w refined>",
  "top_risk":           "<≤25w risk to consensus playing out>",
  "risk_mit":           "<≤25w>",
  "thesis_status":      "IN_PLAY | WATCH | INVALIDATED",
  "thesis_div":         "HIGH | MED | LOW",
  "regime":             "risk-on | risk-off | mixed | transitioning",
  "regime_confidence":  0.65,
  "macro_predictions":  [ /* 2-5 entries, refined consensus-side predictions */ ],
  "refinement_rationale": "<1-2 sentences. cite specific tool result(s) AND/OR skeptic critique points. Audit trail.>"
}

## RULES (strict)
1. First char='{'. Last char='}'. No text outside JSON.
2. Same path-neutral field rules as consensus.analyst — PARADOX CHECK (consensus version: positioning/flow/anchoring), regime, regime_confidence, DATE TENSE, NUMERIC GROUNDING, PROSE FORMAT.
3. priced_explanation enum (consensus): structural_flow | hedging_demand | passive_indexing | momentum_chasing | rate_anchoring | fed_credibility | crowded_consensus | other.
4. factual_error skeptic cites MANDATORY-fix.
5. macro_predictions: 2-5 entries. predicted_observables ≥3 entries spanning ≥2 asset classes. is_headline 3-5 marks total.
5a. MODE CONTRACT (catalyst vs trend faithfulness — single most common drop reason in this stage): prediction_mode='catalyst' MUST populate ALL 7 catalyst fields (catalyst_description, catalyst_source, predicted_catalyst_date ISO ≥ TODAY, catalyst_date_ci_low_days int, catalyst_date_ci_high_days int, catalyst_probability 0-1, post_catalyst_window_days ≥0) AND set horizon_days/_low/_high = null. prediction_mode='trend' MUST populate horizon_days/_low/_high (positive int) AND set ALL 7 catalyst_* fields = null. If you provided horizon_days and ZERO catalyst_* fields, the prediction is trend — tag it 'trend' verbatim, do not tag 'catalyst' aspirationally. Mis-tagged predictions are dropped with SCHEMA_REJECT. PARTIAL FILLS (5 of 7 catalyst fields set, 2 missing) ALSO drop — Zod rejects all-or-nothing; the writer does not fabricate the missing catalyst_probability / post_catalyst_window_days. Set ALL 7 or set NONE and use trend mode.
5b. INDICATOR VOCAB. \`predicted_observables[].indicator\` MUST be a Yahoo ticker form (length ≤10 chars, uppercase + digits + ".-" only, optional "$" crypto prefix). Allowed examples: SPY QQQ IWM XLF XLK NVDA TNX TYX TLT IEF HYG LQD GLD USO CPER PDBC DXY VIX EURUSD $BTC $ETH $SOL. FORBIDDEN: FRED series IDs (DGS10, FEDFUNDS, T10YIE, DFII10, HYOAS) — Yahoo can't probe them, universe validator drops. FORBIDDEN: long-form names (breakeven_inflation_10y, federal_funds_rate, HY_spread) — exceed length cap, Zod drops. The writer-boundary resolver auto-translates "gold"→GLD, "oil"→USO, "10y_yield"→TNX, "vix_term_structure"→VIX as a courtesy; emit canonical directly when possible.
5c. OBSERVABLE = STATE LEVEL, NOT FLOW. \`baseline_value\` MUST be a positive scalar representing the indicator's CURRENT LEVEL (price, yield, spread, index value). FORBIDDEN as observables: weekly flow metrics (crypto_etf_weekly_flow, ETF net flow, fund flow, AUM change), period-over-period deltas, count-of-events, and anything else whose baseline can legitimately be zero or negative. If a flow argument supports your thesis, put it in basis.evidence / thesis_paragraph / refinement_rationale — NOT as a predicted_observable. Zod rejects baseline_value ≤ 0 and the prediction drops.
6. refinement_rationale REQUIRED.
7. STEEL-MAN AWARENESS preserved through refine — don't quietly capitulate to skeptic on every point. Adjudicate.
8. probability: PERCENT (0-100) conviction. 50 = coin-flip; 80 = high. NOT a 0-1 decimal. Decimals OK. D38 (Session 5T) dropped int-only.

## TOOL RESULT QUALITY GUIDANCE
- Tool data confirming consensus → tighten predicted_observables CI bounds, hold headline.
- Tool data with material divergence → REWRITE affected fields, cite in refinement_rationale.
- Tool data error/missing → call out in refinement_rationale (e.g. "news_search for ETF flow data returned empty — basis.evidence held but probability downgraded 10pts").
`;

export interface MConsensusRefineInput {
  readonly macro:           Record<string, unknown>;
  readonly own_draft:       Record<string, unknown>;
  readonly own_skeptic:     Record<string, unknown>;
  readonly toolResults:     ReadonlyArray<{
    readonly name?:       string;
    readonly tool_name?:  string;
    readonly source?:     string;
    readonly result?:     unknown;
    readonly error?:      string;
    readonly status?:     string;
  }>;
  readonly grounding?:      WorldStateGrounding;
  readonly news?:           ReadonlyArray<LlmNewsItem>;
}

function fmt(v: unknown): string {
  if (v == null) return 'not set';
  if (typeof v === 'string') return v.length === 0 ? 'not set' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function formatToolResults(results: MConsensusRefineInput['toolResults']): string {
  if (!results || results.length === 0) return 'No tool results — tools not requested or all returned errors.';
  return results.map((r) => {
    const label = r.name ?? r.tool_name ?? 'unknown';
    if (r.error) return `[${label}] ERROR: ${r.error}`;
    const payload = r.result ?? r;
    return `[${label}] ${JSON.stringify(payload).slice(0, 500)}`;
  }).join('\n\n');
}

function formatSkeptic(s: Record<string, unknown> | undefined): string {
  if (!s || Object.keys(s).length === 0) return '  (no skeptic critique — refine on tool data + draft only)';
  const conv = typeof s['conviction'] === 'string' ? s['conviction'] : 'unknown';
  const reas = typeof s['reasoning']  === 'string' ? s['reasoning']  : 'not provided';
  const ev = Array.isArray(s['disconfirming_evidence']) ? s['disconfirming_evidence'] : [];
  const evLines = ev.length > 0
    ? ev.map((e, i) => {
        const eo = e as Record<string, unknown>;
        return `    [${i + 1}] (${String(eo['cite'] ?? '?')}) ${String(eo['point'] ?? '?')}`;
      }).join('\n')
    : '    (none)';
  const xfc = Array.isArray(s['cross_fed_counter']) ? s['cross_fed_counter'] : [];
  const xfcLine = xfc.length > 0 ? `    ${xfc.map((x) => String(x)).join('; ')}` : '    (none)';
  return `  conviction: ${conv}
  reasoning:  ${reas}
  disconfirming_evidence:
${evLines}
  cross_fed_counter (contrarian draft points cited):
${xfcLine}`;
}

function draftExcerpt(d: Record<string, unknown> | undefined): string {
  if (!d || Object.keys(d).length === 0) return '  (no analyst draft provided)';
  return [
    `  thesis_headline:    ${fmt(d['thesis_headline'])}`,
    `  thesis_paragraph:   ${fmt(d['thesis_paragraph'])}`,
    `  basis:              ${fmt(d['basis'])}`,
    `  priced_explanation: ${fmt(d['priced_explanation'])}`,
    `  probability:        ${fmt(d['probability'])}`,
    `  horizon:            ${fmt(d['horizon'])}`,
    `  conditional:        ${fmt(d['conditional'])}`,
    `  cascade_prose:      ${fmt(d['cascade_prose'])}`,
    `  cascade_nodes:      ${fmt(d['cascade_nodes'])}`,
    `  cross_portfolio_thread: ${fmt(d['cross_portfolio_thread'])}`,
    `  top_risk:           ${fmt(d['top_risk'])}`,
    `  risk_mit:           ${fmt(d['risk_mit'])}`,
    `  thesis_status:      ${fmt(d['thesis_status'])}`,
    `  thesis_div:         ${fmt(d['thesis_div'])}`,
    `  regime:             ${fmt(d['regime'])}`,
    `  regime_confidence:  ${fmt(d['regime_confidence'])}`,
    `  macro_predictions:  ${d['macro_predictions'] === undefined ? 'not set' : JSON.stringify(d['macro_predictions']).slice(0, 1500)}`,
  ].join('\n');
}

export function buildUserPrompt(input: MConsensusRefineInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  const macroLines = Object.entries(input.macro ?? {})
    .filter(([k]) => typeof k === 'string' && !k.startsWith('_'))
    .slice(0, 30)
    .map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n');

  return `${groundingBlock}MACRO PAYLOAD:
${macroLines || '  (empty)'}

OWN_DRAFT (consensus.analyst — refine this):
${draftExcerpt(input.own_draft)}

OWN_SKEPTIC critique:
${formatSkeptic(input.own_skeptic)}

TOOL RESULTS (merged + deduped across contrarian + consensus paths by dispatcher):
${formatToolResults(input.toolResults)}

MACRO NEWS (last 48h):
${renderNewsBlock(input.news ?? [])}

REQUEST: emit_m_consensus_refine

Emit a SINGLE JSON object matching the schema. FULL refined thesis — not a DIFF. factual_error skeptic cites MANDATORY-fix. refinement_rationale REQUIRED.`;
}
